import sanitizeHtml from 'sanitize-html';
import {
  ALLOW_EMBEDDED_URLS,
  // UP_TO_DATE_SYSID,
  UP_TO_DATE_URL,
} from 'shared/constants';
import { ANALYTICS_CATEGORIES_TYPES, CATEGORY_TYPES } from '../constants';
import _ from 'lodash';
import sc from 'string-comparison';

async function asyncForEach(array, callback) {
  for (let index = 0; index < array.length; index++) {
    // eslint-disable-next-line no-await-in-loop
    await callback(array[index], index, array);
  }
}

const timeout = ms => new Promise(resolve => setTimeout(resolve, ms));

const toBase64 = text => Buffer.from(text).toString('base64');

const queryString = params =>
  Object.keys(params)
    .map(key => key + '=' + encodeURIComponent(params[key]))
    .join('&');

const convertToSec = (time = Date.now()) => (time / 1000) | 0;

const nowSEC = () => (Date.now() / 1000) | 0;

const getParentIdByPath = (path = '') => {
  if (!path) return null;

  return path.split(',').pop();
};

const camelCaseToWords = function (str = '') {
  return str
    .replace(/_/g, ' ') // Replace all underscores with spaces
    .toLowerCase()
    .replace(/\b\w/g, char => char.toUpperCase()); // Capitalize first letter of each word
};

const removeDiacritics = str => {
  return str?.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

// Function to escape special characters in the string to make it safe for RegExp
const escapeRegExp = str => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // Escapes special characters
};

const normalizedContains = (haystack, needle) => {
  const safeNeedle = escapeRegExp(removeDiacritics(needle)); // Escape special characters in needle
  const regExp = new RegExp(safeNeedle, 'gi');
  return regExp.test(removeDiacritics(haystack));
};

const isUndefinedOrNull = value => value === undefined || !value;

const sortByAlphabet = (a, b) => {
  if (isUndefinedOrNull(a) && isUndefinedOrNull(b)) return 0;
  if (isUndefinedOrNull(a)) return 1;
  if (isUndefinedOrNull(b)) return -1;
  return a.localeCompare(b);
};

const formatNumber = num => {
  if (num === null || num === undefined) return '0';
  return num.toLocaleString('en-US');
};

const parseYoutubeUrl = link => {
  const standardPattern =
    /^https?:\/\/(?:www\.)?youtube\.com\/watch\?(?:.*&)?v=(?<videoId>[^\s&]+)/;
  const shortenedPattern = /^https?:\/\/youtu.be\/(?<videoId>[^\s&]+)/;

  const standardMatch = link.match(standardPattern);
  const shortenedMatch = link.match(shortenedPattern);

  const EMBED_LINK = 'https://www.youtube.com/embed/';

  if (standardMatch && standardMatch.groups.videoId) {
    const { videoId } = standardMatch.groups;
    return `${EMBED_LINK}${videoId}`;
  } else if (shortenedMatch && shortenedMatch.groups.videoId) {
    const { videoId } = shortenedMatch.groups;
    return `${EMBED_LINK}${videoId}`;
  }

  // Invalid YouTube link
  return '';
};

const isVideoUrl = (url = '') => {
  // we can later add more video providers like vimeo
  return /youtube.com\/watch|youtu.be/.test(url);
};

const isGoogleUrl = url => {
  return /docs.google.com\/(spreadsheets|presentation|document)/.test(url);
};

const isVideoByFormat = link => {
  const videoFormats = [
    'mp4',
    'mov',
    'avi',
    'wmv',
    'flv',
    '3gp',
    'webm',
    'mkv',
    'm4a',
    'm4v',
    'mp3',
  ];
  const format = link?.split('.')[link?.split('.').length - 1];
  return videoFormats.includes(format);
};

// same sanitization rules as in backend
const sanitizeHtmlContent = (html, config) => {
  const localConfig = {
    allowedURLs: ALLOW_EMBEDDED_URLS,
    ...config,
  };

  function isURLAllowed(url) {
    // Check if any allowedURL is the beginning of the URL
    return localConfig.allowedURLs.some(allowedURL => url.startsWith(allowedURL));
  }

  return sanitizeHtml(html, {
    allowedTags: sanitizeHtml.defaults.allowedTags.concat(['img', 'video', 'source', 'iframe']),
    allowedAttributes: {
      p: ['style'],
      h1: ['style'],
      h2: ['style'],
      h3: ['style'],
      ul: ['style'],
      span: ['style'],
      div: ['style'],
      a: ['href', 'name', 'target'],
      img: ['style', 'src', 'alt', 'title', 'width', 'height'],
      td: ['colspan', 'rowspan'],
      th: ['colspan', 'rowspan'],
      tr: ['colspan', 'rowspan'],
      table: ['cellspacing', 'cellpadding'],
      video: ['style', 'controls', 'src'],
      source: ['src', 'type'],
      iframe: ['src', 'allowfullscreen'],
    },
    allowedSchemesAppliedToAttributes: ['href', 'src'],
    transformTags: {
      a: sanitizeHtml.simpleTransform('a', { target: '_blank', rel: 'noopener noreferrer' }),
      iframe: function (tagName, attribs) {
        // Only allow certain trusted domains for iframe src
        if (attribs.src && isURLAllowed(attribs.src)) {
          return {
            tagName: tagName,
            attribs: {
              ...attribs,
              src: attribs.src,
            },
          };
        }
        // remove iframe if it doesn't meet the criteria
        return false;
      },
    },
    parseStyleAttributes: false,
  });
};

const getCookie = ({ name }) =>
  document.cookie
    .split('; ')
    .find(cookie => cookie.includes(name))
    ?.split('=')[1];

const getIdfromUrl = ({ url = '' }) => url.split('/').pop();

const getParamFromQuery = ({ url = '', paramName = 'id' }) => {
  const params = new URLSearchParams(url?.split('?')[1]);

  return params.get(paramName);
};

const generateUpToDateLink = (searchTerm, unId) => {
  const params = new URLSearchParams(
    _.omitBy(
      {
        search: searchTerm,
        // unid: unId,
        // srcsys: UP_TO_DATE_SYSID,
      },
      _.isEmpty,
    ),
  );

  const link = `${UP_TO_DATE_URL}?${params.toString()}`;

  return link;
};

const isAppUrl = url => {
  const appUrlRegex = /^https:\/\/(dev\.|staging\.)?app\.(headtotoe\.io|c8\.health|c8health\.com)/;
  return url.match(appUrlRegex)?.length;
};

const formatFullName = (user = {}, options = {}) => {
  const { firstName, lastName, email } = user || {};
  const { withEmail = false, labelInParenthesis = null } = options || {};

  if (!firstName && !lastName) {
    return email || '';
  }

  const emailSufix = withEmail && email ? ` (${email})` : '';

  const parenthesisSufix = labelInParenthesis ? ` (${labelInParenthesis})` : '';

  return `${firstName ?? ''} ${lastName ?? ''}`.trim().concat(emailSufix).concat(parenthesisSufix);
};

const formatInitials = (user = {}) => {
  const { firstName, lastName } = user || {};

  if (!firstName && !lastName) {
    return '';
  }

  return `${firstName?.charAt(0).toUpperCase() ?? ''}${lastName?.charAt(0).toUpperCase() ?? ''}`;
};

const getCategoryType = ({ path, type, isNew, isSectionCreation }) => {
  if (isNew) {
    if (isSectionCreation) {
      return ANALYTICS_CATEGORIES_TYPES.SECTION;
    } else if (!isSectionCreation && type === CATEGORY_TYPES.CATEGORY) {
      return ANALYTICS_CATEGORIES_TYPES.DIRECTORY;
    } else {
      return ANALYTICS_CATEGORIES_TYPES.BINDER;
    }
  } else {
    const hasRoot = new RegExp(/ROOT/gi).test(path);
    let pathPartsLen = path.split(',').length;
    pathPartsLen = hasRoot ? pathPartsLen : pathPartsLen + 1;
    if (type === CATEGORY_TYPES.CATEGORY && pathPartsLen % 2 === 0) {
      return ANALYTICS_CATEGORIES_TYPES.SECTION;
    } else if (type === CATEGORY_TYPES.CATEGORY && pathPartsLen % 2 !== 0) {
      return ANALYTICS_CATEGORIES_TYPES.DIRECTORY;
    } else {
      return ANALYTICS_CATEGORIES_TYPES.BINDER;
    }
  }
};

function normalizeText(text) {
  return text
    .toLowerCase()
    .replace(/[^\w\s]/g, '')
    .trim();
}

/**
 * Given two strings, this function calculates the similarity between them
 * @param {string} text1 - the first string to compare
 * @param {string} text2 - the second string to compare
 * @param {object} [options] - Configuration options
 * @param {number} [options.threshold=0.7] - Threshold for combined similarity score
 * @param {number} [options.shortTextThreshold=4] - Number of words to consider a text short
 * @returns {boolean} true if the strings are similar, false otherwise
 * @link https://en.wikipedia.org/wiki/Cosine_similarity the cosine algorithm used to calculate the similarity on a long text
 * @link https://en.wikipedia.org/wiki/Jaro%E2%80%93Winkler_distance the jaro-winkler algorithm used to calculate the similarity on a short text
 */
function isStringsSimilar(text1, text2, { shortTextThreshold = 4, threshold = 0.7 } = {}) {
  // Normalize texts
  const normalizedText1 = normalizeText(text1);
  const normalizedText2 = normalizeText(text2);

  // Determine if it's a short text
  const isShort = text1.split(/\s+/).length < shortTextThreshold;

  // Calculate similarity scores
  const cosineScore = sc.cosine.similarity(normalizedText1, normalizedText2);
  const jaroWinklerScore = sc.jaroWinkler.similarity(normalizedText1, normalizedText2);

  // Adjust weights based on text length
  const jaroWinklerWeight = isShort ? 0.85 : 0.4;
  const cosineWeight = 1 - jaroWinklerWeight;

  // Calculate combined score
  const combinedScore = cosineScore * cosineWeight + jaroWinklerScore * jaroWinklerWeight;

  // Return whether combined score meets threshold
  return combinedScore >= threshold;
}

function getImageDimensions(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = url;

    img.onload = function (ev) {
      const { width, height } = ev.target;
      const aspectRatio = width / height;
      resolve({
        aspectRatio,
        width,
        height,
      });
    };

    img.onerror = function () {
      reject(new Error('Failed to load image.'));
    };
  });
}
/**
 * Generate a hash for a given file
 * @async
 * @param {File} file - The file to hash
 * @return {Promise<string>} the hash string
 * @description This function reads the file as an ArrayBuffer, computes the SHA-256 hash using the SubtleCrypto API, and converts the hash to a hexadecimal string.
 */
const generateHash = async file => {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(hashBuffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
};

function sortArraysDeep(value) {
  if (Array.isArray(value)) {
    return [...value].sort().map(sortArraysDeep);
  }
  if (typeof value === 'object' && value !== null) {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, sortArraysDeep(v)]));
  }
  return value;
}
function getChangedFields(original, updated) {
  const normalizedOriginal = sortArraysDeep(original);
  const normalizedUpdated = sortArraysDeep(updated);

  return Object.keys(normalizedOriginal).reduce((changes, key) => {
    if (
      !_.isEqual(normalizedOriginal[key], normalizedUpdated[key]) &&
      normalizedUpdated[key] !== undefined
    ) {
      console.log(key);
      changes[key] = { from: normalizedOriginal[key], to: normalizedUpdated[key] };
    }
    return changes;
  }, {});
}
export {
  asyncForEach,
  timeout,
  toBase64,
  queryString,
  convertToSec,
  nowSEC,
  getParentIdByPath,
  camelCaseToWords,
  removeDiacritics,
  normalizedContains,
  sortByAlphabet,
  parseYoutubeUrl,
  isVideoUrl,
  isGoogleUrl,
  isVideoByFormat,
  sanitizeHtmlContent,
  getCookie,
  getIdfromUrl,
  getParamFromQuery,
  generateUpToDateLink,
  isAppUrl,
  formatFullName,
  formatInitials,
  getCategoryType,
  isStringsSimilar,
  getImageDimensions,
  generateHash,
  sortArraysDeep,
  getChangedFields,
  formatNumber,
};
